/** 本地时区下将 Unix 秒时间戳格式化为 YYYY-MM-DD */
export function formatDateKeyLocal(tsSeconds: number): string {
  const d = new Date(tsSeconds * 1000)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
}

export function endOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 0)
}

function toUnixSeconds(d: Date): number {
  return Math.floor(d.getTime() / 1000)
}

export type DashboardQuickPreset =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'thisMonth'
  | 'custom'

/** 有对应快捷起止时间计算的预设（不含「仅选手动」的 custom） */
export type PresetWithQuickRange = Exclude<DashboardQuickPreset, 'custom'>

export interface TimeRange {
  start: number
  end: number
}

/** 今日 00:00:00 至「当前时间」（看板顶栏「今日」指标用） */
export function getTodaySoFarRange(now = new Date()): TimeRange {
  const start = toUnixSeconds(startOfLocalDay(now))
  const end = toUnixSeconds(now)
  return { start, end }
}

/** 单日内完整区间 00:00:00 - 23:59:59 */
export function getCalendarSingleDayRange(day: Date, now = new Date()): TimeRange {
  const start = toUnixSeconds(startOfLocalDay(day))
  const endD = endOfLocalDay(day)
  const endCap = endD.getTime() > now.getTime() ? now : endD
  return { start, end: toUnixSeconds(endCap) }
}

/**
 * 快捷时间范围：今天 / 昨天 / 近7天 / 近30天 / 本月
 * 规则与产品约定一致：起止为本地日界，结束日取 min(当夜 23:59:59, 现在) 避免未来时刻。
 */
export function getQuickTimeRange(
  preset: PresetWithQuickRange,
  now = new Date()
): TimeRange {
  const today0 = startOfLocalDay(now)

  switch (preset) {
    case 'today': {
      return {
        start: toUnixSeconds(today0),
        end: toUnixSeconds(endOfLocalDay(now)),
      }
    }
    case 'yesterday': {
      const y = new Date(today0)
      y.setDate(y.getDate() - 1)
      return { start: toUnixSeconds(startOfLocalDay(y)), end: toUnixSeconds(endOfLocalDay(y)) }
    }
    case 'last7': {
      const startD = new Date(today0)
      startD.setDate(startD.getDate() - 6)
      return { start: toUnixSeconds(startD), end: toUnixSeconds(endOfLocalDay(now)) }
    }
    case 'last30': {
      const startD = new Date(today0)
      startD.setDate(startD.getDate() - 29)
      return { start: toUnixSeconds(startD), end: toUnixSeconds(endOfLocalDay(now)) }
    }
    case 'thisMonth': {
      const startD = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0)
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 0)
      return { start: toUnixSeconds(startD), end: toUnixSeconds(last) }
    }
    default: {
      const _ex: never = preset
      return _ex
    }
  }
}

const QUICK_PRESETS: PresetWithQuickRange[] = [
  'today',
  'yesterday',
  'last7',
  'last30',
  'thisMonth',
]

const MATCH_EPS = 2

/**
 * 判断当前 (start,end) 是否与某一快捷范围一致（用于高亮/取消高亮）
 */
export function findMatchingQuickPreset(
  start: number,
  end: number,
  now = new Date()
): PresetWithQuickRange | null {
  for (const p of QUICK_PRESETS) {
    const { start: s, end: e } = getQuickTimeRange(p, now)
    if (Math.abs(start - s) <= MATCH_EPS && Math.abs(end - e) <= MATCH_EPS) {
      return p
    }
  }
  return null
}

export interface DashboardFilterState {
  start: number
  end: number
  activePreset: DashboardQuickPreset
  /** null = 全部 API Key */
  tokenId: number | null
}

export function getDefaultFilterRange(): DashboardFilterState {
  const r = getQuickTimeRange('last7', new Date())
  return { ...r, activePreset: 'last7', tokenId: null }
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** 本地 YYYY-MM-DD HH:mm:ss，用于筛选条展示与 DatePicker */
export function formatDateTimeLocalSeconds(ts: number): string {
  const d = new Date(ts * 1000)
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

export function parseDateTimeLocalToSeconds(s: string): number | null {
  const trimmed = s.trim()
  if (!trimmed) return null
  const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T')
  const d = new Date(normalized)
  if (Number.isNaN(d.getTime())) return null
  return Math.floor(d.getTime() / 1000)
}
